"use client";

import { useEffect, useSyncExternalStore } from "react";

/**
 * "View as a free user" — a display-only admin affordance modeled exactly on
 * the existing `/?preview=landing` toggle in HomePageSwitcher. Turning it on
 * flips two things and nothing else: HomePageSwitcher's choice of home (it
 * renders FreeHome instead of CreatorHome) and AppHeader's nav branch (it
 * shows the nav a free-plan user would see instead of the subscriber nav).
 *
 * The flag survives client-side navigation (sessionStorage), so it keeps
 * applying on `/hub`, `/my-projects`, tool routes, etc. — not just while
 * `?preview=free` is literally in the URL. sessionStorage (not localStorage
 * or a cookie) is the store on purpose: it dies with the tab, matches the
 * "preview, not a real state change" nature of the toggle, never rides along
 * on a server request, and can't leak into a different browser session.
 *
 * Why this can never widen access:
 *  - Real entitlement is decided server-side in src/lib/server/authz.ts
 *    (`loadAccessContext`, `requireAdmin`, `requireToolAccess`), in
 *    app/(tools)/_guard.ts, and in app/(admin)/layout.tsx. None of those
 *    import this module or read the stored flag — it never reaches a server
 *    authorization call, so it cannot grant or hide real access anywhere it
 *    matters.
 *  - `useIsPreviewingFreeUser` only ever returns `true` for a caller that
 *    already passes a real, server-verified `isAdmin === true` from
 *    useAuth(). A non-admin who has the sessionStorage key set (by hand, or
 *    because they share a tab with an admin session that never happens in
 *    practice) still gets `false` back — the stored value alone decides
 *    nothing without a real admin also being present.
 *  - It is read fresh on every render via the browser's storage/event
 *    machinery, with no cookie and no reach outside the current tab, so it
 *    can never outlive the tab or leak into a different request/session.
 *
 * Every caller (HomePageSwitcher, FreeHome, AppHeader) re-ANDs this against
 * its own `isAdmin` from useAuth() before acting on it, so admin status and
 * "viewing as free" stay two independent variables throughout — never
 * collapsed into one — which is what keeps a previewing admin from ever
 * being mistaken for an actual free user by code that branches on `isAdmin`
 * (the bug the July attempt hit: admins have no subscription of their own,
 * so treating "not a subscriber" as "free user" bounced admins off
 * /account and /subscription).
 */
export const FREE_PREVIEW_PARAM = "preview";
export const FREE_PREVIEW_VALUE = "free";
export const FREE_PREVIEW_HREF = `/?${FREE_PREVIEW_PARAM}=${FREE_PREVIEW_VALUE}`;

const STORAGE_KEY = "free-preview-active-v1";
const CHANGE_EVENT = "free-preview-change";

function readStoredFlag(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    // sessionStorage can throw (private browsing, blocked storage) — treat
    // as "not previewing" rather than crash the header.
    return false;
  }
}

function writeStoredFlag(active: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (active) {
      window.sessionStorage.setItem(STORAGE_KEY, "1");
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Non-fatal — worst case the preview doesn't persist across navigation.
  }
  // sessionStorage writes don't fire a `storage` event in the *same* tab
  // (only other tabs get that), so broadcast our own event for any other
  // hook instance mounted in this tab (e.g. AppHeader + HomePageSwitcher
  // both rendered at once) to pick up the change immediately.
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(callback: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}

function getServerSnapshot(): boolean {
  return false;
}

/**
 * Whether the free-user preview is active. `isAdmin` must be the real,
 * server-verified value from useAuth() — passing `false` always returns
 * `false`, regardless of the URL or sessionStorage.
 *
 * Entering via the `?preview=free` link (FREE_PREVIEW_HREF) persists the
 * flag to sessionStorage so it survives the next client-side navigation.
 */
export function useIsPreviewingFreeUser(isAdmin: boolean): boolean {
  const stored = useSyncExternalStore(subscribe, readStoredFlag, getServerSnapshot);

  // The entry param is read off `window.location`, in an effect, rather than
  // through `useSearchParams`. That hook opts its whole page out of static
  // prerendering — Next bails the nearest Suspense boundary to client
  // rendering — and this hook now runs inside `AppHeader`, i.e. on every page
  // including the `/free` tools, whose whole point is to be static HTML a
  // crawler can read without running JavaScript. Nothing here needs the
  // server: the flag is a client-only display preference, and reading it one
  // paint later is invisible next to the auth state it is gated on anyway.
  useEffect(() => {
    if (!isAdmin) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get(FREE_PREVIEW_PARAM) === FREE_PREVIEW_VALUE) {
      writeStoredFlag(true);
    }
  }, [isAdmin]);

  return isAdmin && stored;
}

/**
 * Turns the preview on directly, for the header's "Preview as free" control.
 *
 * The control sets the flag itself rather than relying on navigating to
 * `?preview=free`: an admin clicking it while already on `/` changes only the
 * query string, so the header never remounts, the entry effect above never
 * re-runs, and nothing happens — which is exactly what it did. The URL stays a
 * working entry point for a fresh load or a pasted link; this is the path for
 * a click.
 *
 * The caller is responsible for being a real admin — every reader re-checks
 * `isAdmin` anyway, so a stored flag on its own still changes nothing.
 */
export function enterFreePreview(): void {
  writeStoredFlag(true);
}

/**
 * Clears the persisted free-preview flag. Used by the header's "Exit"
 * escape hatch. Safe to call even if nothing was ever stored.
 */
export function clearFreePreview(): void {
  writeStoredFlag(false);
}
